import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Modal, Stack, SvgIcon, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import IndexMap from './IndexMap';
import IndexList from './IndexList';
import IndexCalendar from './IndexCalendar';
import TopAppBar from './TopAppBar';
import ViewSelectionDialog from '../ui/ViewSelectionDialog';
import { ActionButton, SurfaceCard } from '../ui/ConvivialComponents';
import { getGroupsNear } from '../../services/firebaseData';
import { getConvivialNativeLocation } from '../../services/nativeLocation';
import { requestNativeLogin } from '../../services/nativeAppBridge';

export default function IndexTopBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = localStorage.getItem('user_id');
    const permissionLocation = localStorage.getItem('permission_location');
    const [iconSpinning, setIconSpinning] = useState(false);
    const [view, setView] = useState('map');
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [latLon, setLatLon] = useState({
        lat: parseFloat(localStorage.getItem('lastLat')) || 45.4642,
        lng: parseFloat(localStorage.getItem('lastLng')) || 9.1900
    });
    const [pins, setPins] = useState([]);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [openModal, setOpenModal] = useState(permissionLocation !== 'granted');

    const handleGeolocation = useCallback(({ showModalOnFailure = true } = {}) => {
        setOpenModal(false);
        setIconSpinning(true);

        const nativeLocation = getConvivialNativeLocation();

        if (nativeLocation) {
            const nextLocation = {
                lat: nativeLocation.lat,
                lng: nativeLocation.lng,
            };

            localStorage.setItem('permission_location', 'granted');
            setCurrentLocation(nextLocation);
            setLatLon(nextLocation);
            setIconSpinning(false);
            return;
        }

        setTimeout(() => {
            if (!navigator.geolocation) {
                setIconSpinning(false);
                if (showModalOnFailure) setOpenModal(true);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const nextLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    localStorage.setItem('permission_location', 'granted');
                    setCurrentLocation(nextLocation);
                    setLatLon(nextLocation);
                    setIconSpinning(false);
                },
                () => {
                    setIconSpinning(false);
                    if (showModalOnFailure) setOpenModal(true);
                }
            );
        }, 100);
    }, []);

    useEffect(() => {
        handleGeolocation({ showModalOnFailure: permissionLocation !== 'granted' });
    }, [handleGeolocation, location.pathname, permissionLocation]);

    useEffect(() => {
        localStorage.setItem('lastLat', latLon.lat);
        localStorage.setItem('lastLng', latLon.lng);

        const fetchPins = async () => {
            try {
                const groups = await getGroupsNear({
                    latitude: latLon.lat,
                    longitude: latLon.lng,
                    radiusKm: process.env.REACT_APP_DEFAULT_RADIUS_KM,
                    userId: userId || null,
                });
                setPins(groups);
            } catch (error) {
                console.error('Error fetching pins:', error);
            }
        };

        fetchPins();
    }, [latLon, userId]);

    const handleLoginClick = () => {
        const handledByNative = requestNativeLogin('/index');

        if (!handledByNative) {
            navigate('/login');
        }
    };

    return (
        <>
            <TopAppBar
                title="ConvivialApp.it"
                showAvatar={!!userId}
                titleAction={
                    <IconButton
                        aria-label="Cambia visualizzazione"
                        aria-haspopup="dialog"
                        aria-expanded={viewDialogOpen}
                        onClick={() => setViewDialogOpen(true)}
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'primary.container',
                            color: '#fff',
                            boxShadow: '0 6px 16px rgba(97,0,31,0.15)',
                            '&:hover': { bgcolor: 'primary.main' },
                        }}
                    >
                        <SvgIcon sx={{ fontSize: 28 }}>
                            <path
                                d="m4 10 8-6 8 6-8 6-8-6Z M4 15l8 6 8-6 M19 2v6 M16 5h6"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.9"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </SvgIcon>
                    </IconButton>
                }
                rightAction={!userId && (
                    <Button
                        onClick={handleLoginClick}
                        sx={{ color: 'primary.main', fontWeight: 800 }}
                    >
                        Accedi
                    </Button>
                )}
            />

            <ViewSelectionDialog
                open={viewDialogOpen}
                selectedView={view}
                onClose={() => setViewDialogOpen(false)}
                onSelect={(nextView) => {
                    setView(nextView);
                    setViewDialogOpen(false);
                }}
            />

            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <SurfaceCard
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 420,
                    }}
                >
                    <Typography variant="h3" sx={{ color: 'primary.main', mb: 1 }}>
                        Tavoli vicino a te
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        Usa la posizione per scoprire incontri conviviali nella tua zona. Puoi esplorare anche senza accedere.
                    </Typography>
                    <Stack spacing={1.5}>
                        <ActionButton onClick={handleGeolocation}>Usa la mia posizione</ActionButton>
                        <Button onClick={() => setOpenModal(false)} sx={{ color: 'text.secondary' }}>
                            Continua senza condividere la posizione
                        </Button>
                    </Stack>
                </SurfaceCard>
            </Modal>

            <Box sx={{ pt: 'var(--cv-top-bar-height)', pb: 'var(--cv-bottom-nav-height)' }}>
                <Box sx={{ display: view === 'map' ? 'block' : 'none' }}>
                    <IndexMap
                        latLon={latLon}
                        setLatLon={setLatLon}
                        pins={pins}
                        iconSpinning={iconSpinning}
                        currentLocation={currentLocation}
                    />
                </Box>
                <Box sx={{ display: view === 'list' ? 'block' : 'none' }}>
                    <IndexList pins={pins} />
                </Box>
                <Box sx={{ display: view === 'calendar' ? 'block' : 'none' }}>
                    <IndexCalendar pins={pins} />
                </Box>
            </Box>
        </>
    );
}
